import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import {
  formatDate,
  formatDateAndTime,
  formatTime,
} from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { ISlot } from "./VisitBooking.types";

/** date-fns patterns, so the day and month names follow the user's language. */
const DAY_KEY_PATTERN = "yyyy-MM-dd";
const DAY_LABEL_PATTERN = "EEEE d MMMM";

/**
 * The main applicant's name, empty when the candidacy carries no contact at all.
 * Callers render a placeholder for that case rather than an empty gap.
 */
export const getCandidateName = (application: IApplication) => {
  const contact = application.contacts?.[0];

  return contact ? getName(contact) : "";
};

/** Identifies a moment by its exact boundaries, the way the server does. */
export const getSlotKey = ({ start, end }: { start: string; end: string }) =>
  `${new Date(start).getTime()}-${new Date(end).getTime()}`;

export const getDayKey = (date: string) => formatDate(date, DAY_KEY_PATTERN);

export const getDayLabel = (date: string) =>
  formatDate(date, DAY_LABEL_PATTERN);

export const getSlotTimeLabel = ({ start, end }: ISlot) =>
  `${formatTime(start)} - ${formatTime(end)}`;

/** One moment written out in full, for a confirmation the broker reads aloud. */
export const getMomentLabel = (date: string) =>
  formatDateAndTime(date, DAY_LABEL_PATTERN);

/** Groups the moments per day, keeping the order the server sent them in. */
export const groupSlotsPerDay = (slots: ISlot[]) => {
  const days: { key: string; slots: ISlot[] }[] = [];

  slots.forEach(slot => {
    const key = getDayKey(slot.start);
    const day = days.find(candidate => candidate.key === key);

    if (day) {
      day.slots.push(slot);
    } else {
      days.push({ key, slots: [slot] });
    }
  });

  return days;
};

/**
 * The refusal text as the server wrote it.
 *
 * api-next wraps an exception one level deeper than the legacy API does, and the
 * validation pipe sends an array rather than a string, so all four shapes are
 * read here. Without this the caller sees an empty string for every refusal and
 * cannot tell one apart from another.
 */
export const getServerMessage = (error: unknown) => {
  const data = (
    error as {
      response?: {
        data?: {
          exception?: { message?: string; response?: { message?: string } };
          error?: { message?: string };
          message?: string | string[];
        };
      };
    }
  )?.response?.data;

  const message =
    data?.exception?.response?.message ||
    data?.exception?.message ||
    data?.error?.message ||
    data?.message ||
    "";

  return Array.isArray(message) ? message.join(" ") : message;
};

/** The code api-next returns when the moment was taken in the meantime. */
export const VISIT_SLOT_TAKEN = "VISIT_SLOT_TAKEN";

/**
 * Whether this refusal is "someone else just took that moment".
 *
 * The code is what to go on: it is the server saying which case this is,
 * rather than the dashboard guessing from the words of a sentence that is
 * free to be rewritten or translated. The old substring match stays behind
 * it, because a server that has not deployed the code yet still returns the
 * sentence, and a broker should not get the generic error in the window
 * where the two versions overlap (DEV-3328).
 */
export const isSlotTakenError = (error: unknown) => {
  const data = (
    error as {
      response?: { data?: { code?: string; error?: { code?: string } } };
    }
  )?.response?.data;

  if (
    data?.code === VISIT_SLOT_TAKEN ||
    data?.error?.code === VISIT_SLOT_TAKEN
  ) {
    return true;
  }

  return getServerMessage(error).toLowerCase().includes("fully booked");
};

/**
 * Why nothing is bookable, as api-next names it. Each one asks the broker for a
 * different action, which is why each has its own sentence: a calendar conflict
 * used to read as "fully booked" and a property without blocks as "expired",
 * both claiming something that had not happened (DEV-3220).
 */
export const NO_AVAILABILITY_REASON = {
  calendarConflict: "calendar_conflict",
  expired: "expired",
  fullyBooked: "fully_booked",
  notConfigured: "not_configured",
} as const;

const TOOLBAR_WARNING_KEY_PER_REASON: Record<string, string> = {
  [NO_AVAILABILITY_REASON.calendarConflict]:
    "property.applications.availability.calendar_conflict_warning",
  [NO_AVAILABILITY_REASON.expired]:
    "property.applications.availability.expired_warning",
  [NO_AVAILABILITY_REASON.fullyBooked]:
    "property.applications.availability.fully_booked_warning",
  [NO_AVAILABILITY_REASON.notConfigured]:
    "property.applications.availability.no_slots_warning",
};

const EMPTY_STATE_KEY_PER_REASON: Record<string, string> = {
  [NO_AVAILABILITY_REASON.calendarConflict]:
    "visit_booking.empty.calendar_conflict",
  [NO_AVAILABILITY_REASON.expired]: "visit_booking.empty.expired",
  [NO_AVAILABILITY_REASON.fullyBooked]: "visit_booking.empty.fully_booked",
  [NO_AVAILABILITY_REASON.notConfigured]: "visit_booking.empty.none",
};

/**
 * The key for one reason, or the "nothing set up" sentence for a reason this
 * build does not know or a server that sends none. That fallback is the oldest
 * and the most neutral of them, so it is the safe one to show while an older and
 * a newer api-next overlap during a deploy.
 */
const getKeyForReason = (params: {
  keyPerReason: Record<string, string>;
  reason: string | null | undefined;
}) => {
  const { keyPerReason, reason } = params;

  return reason && Object.prototype.hasOwnProperty.call(keyPerReason, reason)
    ? keyPerReason[reason]
    : keyPerReason[NO_AVAILABILITY_REASON.notConfigured];
};

/** The warning under the visit-slot counter in the candidates toolbar. */
export const getToolbarWarningKey = (reason: string | null | undefined) =>
  getKeyForReason({ keyPerReason: TOOLBAR_WARNING_KEY_PER_REASON, reason });

/** The empty state of the modal a broker books a visit in. */
export const getEmptyStateKey = (reason: string | null | undefined) =>
  getKeyForReason({ keyPerReason: EMPTY_STATE_KEY_PER_REASON, reason });
